import { useEffect } from "react";

const HEARTBEAT_MS = 5_000;
// Chrome throttles timers in a tab hidden for 5 minutes to once a minute, so a background tab beats that seldom.
const STALE_AFTER_MS = 180_000;

const thisTab = typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : String(Math.random());

const storageKey = (procurementId: string) => `sm.interview.open.${procurementId}`;

type Presence = Record<string, number>;

const read = (procurementId: string): Presence => {
  try {
    const raw = window.localStorage.getItem(storageKey(procurementId));
    return raw ? (JSON.parse(raw) as Presence) : {};
  } catch {
    return {};
  }
};

const write = (procurementId: string, presence: Presence) => {
  try {
    if (Object.keys(presence).length === 0) window.localStorage.removeItem(storageKey(procurementId));
    else window.localStorage.setItem(storageKey(procurementId), JSON.stringify(presence));
  } catch {
    return;
  }
};

const live = (presence: Presence, now: number): Presence =>
  Object.fromEntries(Object.entries(presence).filter(([, seenAt]) => now - seenAt < STALE_AFTER_MS));

export const useInterviewPresence = (procurementId: string | null) => {
  useEffect(() => {
    if (!procurementId) return;
    const beat = () => {
      const now = Date.now();
      write(procurementId, { ...live(read(procurementId), now), [thisTab]: now });
    };
    const leave = () => {
      const others = live(read(procurementId), Date.now());
      delete others[thisTab];
      write(procurementId, others);
    };
    beat();
    const timer = window.setInterval(beat, HEARTBEAT_MS);
    window.addEventListener("pagehide", leave);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("pagehide", leave);
      leave();
    };
  }, [procurementId]);
};

export const isInterviewOpenElsewhere = (procurementId: string): boolean =>
  Object.keys(live(read(procurementId), Date.now())).some((tab) => tab !== thisTab);
